<script setup lang="ts">
import { CircleCheck, ExternalLink, KeyRound, Plug, Unplug } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { connectUrl, disconnect, useConnections } from "@/api/connections";
import type { ConnectionProvider } from "@/api/contracts";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import CredentialConnectDialog from "@/components/connections/CredentialConnectDialog.vue";
import DeviceConnectDialog from "@/components/connections/DeviceConnectDialog.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Connections" } });

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const connections = useConnections();
const providers = computed(() =>
  [...(connections.data.value ?? [])].sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label)),
);

function rank(p: ConnectionProvider) {
  return p.status === "needs_reauth" || p.status === "error" ? 0 : p.status === "not_connected" ? 1 : p.status === "connected" ? 2 : 3;
}

// Connect links from chat (tool results, whoami) return to ?connected=<id> — also after the
// device dialog finished. Tell the person to go back to the chat; Claude retries from there.
const returnedFrom = ref<string | null>(null);
watch(
  () => [route.query.connected, connections.data.value] as const,
  ([connected, list]) => {
    if (typeof connected !== "string" || !list) return;
    returnedFrom.value = list.find((x) => x.id === connected)?.label ?? connected;
    void router.replace({ query: {} });
  },
  { immediate: true },
);
onMounted(() => {
  const error = route.query.error;
  if (typeof error === "string") {
    toast.error(`Connecting failed: ${error}`);
    void router.replace({ query: {} });
  }
});

// Credential connections (no browser sign-in): a form in a dialog. /connect/<id> sends users here with ?connect=<id>.
const credentialFor = ref<ConnectionProvider | null>(null);
const credentialOpen = ref(false);
function openCredentials(p: ConnectionProvider) {
  credentialFor.value = p;
  credentialOpen.value = true;
}
watch(
  () => [route.query.connect, connections.data.value] as const,
  ([id, list]) => {
    if (typeof id !== "string" || !list) return;
    const p = list.find((x) => x.id === id && x.kind === "credentials" && x.status !== "disabled");
    if (p) openCredentials(p);
    void router.replace({ query: {} });
  },
  { immediate: true },
);

// Device sign-in (AWS IAM Identity Center): /connect/<id> sends users here with ?device=<id>&state=….
const deviceFor = ref<ConnectionProvider | null>(null);
const deviceState = ref<string | null>(null);
const deviceOpen = ref(false);
watch(
  () => [route.query.device, route.query.state, connections.data.value] as const,
  ([id, state, list]) => {
    if (typeof id !== "string" || typeof state !== "string" || !list) return;
    const p = list.find((x) => x.id === id && x.status !== "disabled");
    if (p) {
      deviceFor.value = p;
      deviceState.value = state;
      deviceOpen.value = true;
    }
    // Keep the state out of the address bar and history.
    void router.replace({ query: {} });
  },
  { immediate: true },
);

async function onDisconnect(p: ConnectionProvider) {
  const answer = await confirm({ title: `${t.connections.disconnect} ${p.label}?`, description: t.connections.disconnectConfirm(p.label), confirmLabel: t.connections.disconnect, destructive: true });
  if (!answer) return;
  try {
    await disconnect(p.id);
    toast.success(t.connections.disconnected);
    await queryClient.invalidateQueries({ queryKey: ["connections"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
</script>

<template>
  <div>
    <PageHeader :title="t.connections.title" :description="t.connections.description" />

    <div v-if="returnedFrom" role="status" class="mb-6 flex items-start gap-3 rounded-xl border border-success/30 bg-success-soft p-4 text-sm">
      <CircleCheck class="mt-0.5 size-5 shrink-0 text-success" aria-hidden="true" />
      <div class="flex-1">
        <p class="font-medium">{{ returnedFrom }} is connected.</p>
        <p class="text-muted-foreground">If you came here from Claude, you can close this tab and go back to the chat — say “continue” if Claude doesn't pick it up by itself.</p>
      </div>
      <Button variant="ghost" size="sm" @click="returnedFrom = null">{{ t.common.close }}</Button>
    </div>

    <ListSkeleton v-if="connections.isPending.value" :rows="3" />
    <ErrorState v-else-if="connections.error.value" :error="connections.error.value" @retry="connections.refetch()" />
    <EmptyState v-else-if="providers.length === 0" :icon="Plug" :title="t.connections.empty" description="An admin sets up GitHub, Atlassian and Miro under Admin › Connection providers." />
    <ul v-else class="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
      <li
        v-for="p in providers"
        :key="p.id"
        :class="cn('surface flex flex-col gap-3 p-4 sm:p-5', (p.status === 'needs_reauth' || p.status === 'error') && 'border-warning/60')"
      >
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h2 :class="cn('card-title', p.status === 'disabled' && 'text-muted-foreground')">{{ p.label }}</h2>
            <StatusBadge :status="p.status" />
          </div>
          <p class="mt-1 text-sm text-muted-foreground">{{ p.description }}</p>
        </div>
        <dl v-if="p.status === 'connected' || p.account" class="grid grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-x-4 gap-y-2 text-xs">
          <div v-if="p.account" class="min-w-0"><dt class="text-muted-foreground">{{ t.connections.connectedAs }}</dt><dd class="truncate font-medium" :title="p.account">{{ p.account }}</dd></div>
          <div v-if="p.connectedAt"><dt class="text-muted-foreground">Connected</dt><dd><RelativeTime :value="p.connectedAt" /></dd></div>
          <div v-if="p.lastUsedAt"><dt class="text-muted-foreground">{{ t.connections.lastUsed }}</dt><dd><RelativeTime :value="p.lastUsedAt" /></dd></div>
        </dl>
        <div v-if="p.scopes?.length" class="flex flex-wrap gap-1">
          <Badge v-for="s in p.scopes" :key="s" variant="muted" class="font-mono">{{ s }}</Badge>
        </div>
        <div v-if="p.status !== 'disabled'" class="mt-auto flex flex-wrap gap-2 pt-1">
          <Button v-if="p.status !== 'connected' && p.kind === 'credentials'" size="sm" @click="openCredentials(p)">
            <KeyRound /> {{ p.status === "not_connected" ? t.connections.connect : t.connections.reconnect }}
          </Button>
          <Button v-else-if="p.status !== 'connected'" as="a" :href="connectUrl(p)" size="sm">
            <ExternalLink /> {{ p.status === "not_connected" ? t.connections.connect : t.connections.reconnect }}
          </Button>
          <Button v-if="p.status === 'connected' || p.status === 'needs_reauth' || p.status === 'error'" variant="outline" size="sm" @click="onDisconnect(p)">
            <Unplug /> {{ t.connections.disconnect }}
          </Button>
        </div>
      </li>
    </ul>
    <CredentialConnectDialog v-model:open="credentialOpen" :provider="credentialFor" />
    <DeviceConnectDialog v-model:open="deviceOpen" :provider="deviceFor" :state="deviceState" />
  </div>
</template>
